import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Truck } from 'lucide-react';

const rupee = (n) => `\u20B9${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export const FreightDetailDialog = ({ freightDialog, onClose }) => (
  <Dialog open={!!freightDialog} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="freight-detail-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Truck className="w-4 h-4" style={{ color: '#2B8A4B' }} />
          Freight — {freightDialog?.order?.dealer_name}
        </DialogTitle>
      </DialogHeader>
      {freightDialog && (() => {
        const info = freightDialog.freight || { lines: [], by_dealer_total: 0, by_us_total: 0, total_freight: 0 };
        const lines = info.lines || [];
        const isEstimate = !!info.is_estimate;
        return (
          <div className="space-y-4">
            {isEstimate && (
              <div className="text-xs px-3 py-2 rounded" style={{ background: '#FFF7E6', border: '1px dashed #E89D4F', color: '#8A5A1E' }} data-testid="freight-estimate-note">
                Estimated from the rate card (dealer town × order weight). Transport details appear once the order is dispatched; figures may change at dispatch.
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <Card className="p-3">
                <div className="text-xs" style={{ color: '#5C6B61' }}>Payable by Dealer</div>
                <div className="text-xl font-bold" style={{ color: '#2B8A4B' }} data-testid="freight-dialog-by-dealer">
                  {isEstimate ? '~' : ''}{rupee(info.by_dealer_total)}
                </div>
              </Card>
              <Card className="p-3">
                <div className="text-xs" style={{ color: '#5C6B61' }}>Borne by Us</div>
                <div className="text-xl font-bold" style={{ color: '#C05C35' }}>{isEstimate ? '~' : ''}{rupee(info.by_us_total)}</div>
              </Card>
              <Card className="p-3">
                <div className="text-xs" style={{ color: '#5C6B61' }}>Total Freight</div>
                <div className="text-xl font-bold">{isEstimate ? '~' : ''}{rupee(info.total_freight)}</div>
              </Card>
            </div>

            {lines.length === 0 && (
              <div className="text-sm p-4 text-center rounded" style={{ background: '#FBF9F6', color: '#5C6B61' }} data-testid="freight-no-lines">
                {isEstimate
                  ? 'No freight rate configured for this dealer\u2019s town — set it up under Transport \u2192 Freight rate card to see an estimate.'
                  : 'No freight lines recorded for this order yet.'}
              </div>
            )}
            {lines.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="freight-lines-table">
                  <thead>
                    <tr style={{ background: '#FBF9F6' }}>
                      <th className="p-2 text-left">Dispatched</th>
                      <th className="p-2 text-left">Transporter</th>
                      <th className="p-2 text-left">Vehicle</th>
                      <th className="p-2 text-left">Location</th>
                      <th className="p-2 text-right">Weight (kg)</th>
                      <th className="p-2 text-right">Base</th>
                      <th className="p-2 text-right">Toll</th>
                      <th className="p-2 text-right">Misc</th>
                      <th className="p-2 text-right">Total</th>
                      <th className="p-2 text-right">By Dealer</th>
                      <th className="p-2 text-right">By Us</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((ln, idx) => (
                      <tr key={ln.id || idx} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                        <td className="p-2 whitespace-nowrap">{(ln.dispatched_at || '').slice(0, 10) || '-'}</td>
                        <td className="p-2">{ln.transporter_name || '-'}</td>
                        <td className="p-2 font-mono text-[11px]">{ln.vehicle_number || '-'}</td>
                        <td className="p-2">{ln.location || '-'}</td>
                        <td className="p-2 text-right">{Number(ln.weight_kg_total || 0).toLocaleString('en-IN')}</td>
                        <td className="p-2 text-right">{rupee(ln.base_rate)}</td>
                        <td className="p-2 text-right">{rupee(ln.toll)}</td>
                        <td className="p-2 text-right">{rupee(ln.misc)}</td>
                        <td className="p-2 text-right">{rupee(ln.total_freight)}</td>
                        <td className="p-2 text-right font-semibold" style={{ color: '#2B8A4B' }}>{rupee(ln.by_dealer)}</td>
                        <td className="p-2 text-right" style={{ color: '#C05C35' }}>{rupee(ln.by_us)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#2B5339', color: 'white' }}>
                      <td colSpan="8" className="p-2 text-right font-bold">Total</td>
                      <td className="p-2 text-right font-bold">{rupee(info.total_freight)}</td>
                      <td className="p-2 text-right font-bold">{rupee(info.by_dealer_total)}</td>
                      <td className="p-2 text-right font-bold">{rupee(info.by_us_total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        );
      })()}
    </DialogContent>
  </Dialog>
);
